import { motion, type Variants } from 'framer-motion'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { List } from '@ui/components/List'

import { WalletPaymentProvider } from '@model/WalletPaymentProvider'

import { Notice } from '@shared/components/Notice'
import { PaymentProviderListItem } from '@shared/components/PaymentProviderListItem/PaymentProviderListItem'

import styles from './FundingMethodList.module.scss'

interface DepositMethodListProps {
  methods: WalletPaymentProvider[]
  onSelect(provider: WalletPaymentProvider): void
  disabled?: boolean
  variant?: 'deposit' | 'withdrawal'
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
}

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 10, width: '100%' },
  visible: {
    opacity: 1,
    y: 0,
    width: '100%',
    transition: { type: 'spring', stiffness: 300, damping: 24 },
  },
}

export const FundingMethodList: React.FC<DepositMethodListProps> = (props) => {
  const { methods, onSelect, disabled, variant = 'deposit' } = props
  const { t } = useTranslation()

  return (
    <div className={styles.container}>
      {methods.length > 0 ? (
        <motion.div
          variants={containerVariants}
          className={styles.methodsList}
          initial='hidden'
          animate='visible'
        >
          <List>
            {methods.map((provider) => (
              <motion.div key={provider.method.id} variants={itemVariants}>
                <PaymentProviderListItem
                  provider={provider}
                  disabled={disabled}
                  variant={variant}
                  onClick={() => onSelect(provider)}
                />
              </motion.div>
            ))}
          </List>
        </motion.div>
      ) : (
        <Notice
          id='deposit-methods-empty-info'
          type='info'
          message={t('There are no eligible payment methods for your current selection')}
        />
      )}
    </div>
  )
}
